// Custom Lists database operations
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from '../firebase';

export const customListsDB = {
  // Create a new custom list
  createList: async (userId, { name, drillIds }) => {
    try {
      const listRef = await addDoc(collection(db, 'customLists'), {
        userId,
        name,
        drillIds,
        createdAt: serverTimestamp(),
        lastUsed: serverTimestamp(),
        timesUsed: 1
      });
      return { listId: listRef.id };
    } catch (error) {
      console.error('Error creating custom list:', error);
      throw error;
    }
  },

  // Get all custom lists for a user
  getUserLists: async (userId) => {
    try {
      // Note: This query requires a composite index on (userId, lastUsed)
      // Create it at: https://console.firebase.google.com/v1/r/project/kata-app-c93ab/firestore/indexes
      // Or use the temporary workaround below that sorts in JavaScript
      const q = query(
        collection(db, 'customLists'),
        where('userId', '==', userId),
        orderBy('lastUsed', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        listId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      // If index error, try without orderBy and sort in JavaScript
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        console.warn('Firestore index not created yet. Using JavaScript sort as fallback.');
        try {
          const q = query(
            collection(db, 'customLists'),
            where('userId', '==', userId)
          );
          const querySnapshot = await getDocs(q);
          const lists = querySnapshot.docs.map(doc => ({
            listId: doc.id,
            ...doc.data()
          }));
          
          // Sort by lastUsed in JavaScript (descending - most recent first)
          lists.sort((a, b) => {
            const aTime = a.lastUsed?.toMillis?.() || (a.lastUsed?.seconds ? a.lastUsed.seconds * 1000 : 0) || 0;
            const bTime = b.lastUsed?.toMillis?.() || (b.lastUsed?.seconds ? b.lastUsed.seconds * 1000 : 0) || 0;
            return bTime - aTime; // Descending order
          });
          
          return lists;
        } catch (fallbackError) {
          console.error('Error getting user custom lists (fallback):', fallbackError);
          throw fallbackError;
        }
      }
      console.error('Error getting user custom lists:', error);
      throw error;
    }
  },

  // Get a single custom list by ID
  getList: async (listId) => {
    try {
      const listRef = doc(db, 'customLists', listId);
      const listSnap = await getDoc(listRef);
      if (!listSnap.exists()) {
        return null;
      }
      return {
        listId: listSnap.id,
        ...listSnap.data()
      };
    } catch (error) {
      console.error('Error getting custom list:', error);
      throw error;
    }
  },

  // Update a custom list
  updateList: async (listId, { name, drillIds }) => {
    try {
      const listRef = doc(db, 'customLists', listId);
      const updates = {};
      if (name !== undefined) {
        updates.name = name;
      }
      if (drillIds !== undefined) {
        updates.drillIds = drillIds;
      }
      updates.lastUsed = serverTimestamp();
      updates.timesUsed = increment(1);
      await updateDoc(listRef, updates);
    } catch (error) {
      console.error('Error updating custom list:', error);
      throw error;
    }
  },

  // Delete a custom list
  deleteList: async (listId) => {
    try {
      const listRef = doc(db, 'customLists', listId);
      await deleteDoc(listRef);
    } catch (error) {
      console.error('Error deleting custom list:', error);
      throw error;
    }
  },

  // Update last used timestamp (when list is started)
  updateLastUsed: async (listId) => {
    try {
      const listRef = doc(db, 'customLists', listId);
      await updateDoc(listRef, {
        lastUsed: serverTimestamp(),
        timesUsed: increment(1)
      });
    } catch (error) {
      console.error('Error updating last used:', error);
      throw error;
    }
  }
};

